import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { motion } from 'framer-motion';
import {
  Moon, Lightning, Bed, Smiley, Check, Spinner, ArrowLeft
} from '@phosphor-icons/react';

const CheckInPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ mood: 5, sleep_hours: 7, sleep_quality: 5, energy: 5, symptoms: [], notes: '' });
  const [loading, setLoading] = useState(false);

  const steps = [
    { key: 'mood', label: 'How is your mood today?', min: 1, max: 10, icon: Smiley },
    { key: 'sleep_hours', label: 'How many hours did you sleep?', min: 0, max: 12, icon: Bed },
    { key: 'sleep_quality', label: 'How was your sleep quality?', min: 1, max: 10, icon: Moon },
    { key: 'energy', label: 'What is your energy level?', min: 1, max: 10, icon: Lightning },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/logs`, data);
      navigate('/home');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const currentStep = steps[step];
  const Icon = currentStep?.icon;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="glass-header sticky top-0 z-10 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/home')} className="text-slate-600 hover:text-slate-900">
          <ArrowLeft size={24} />
        </button>
        <h1 className="font-medium text-slate-900">Daily Check-in</h1>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {step < steps.length ? (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="w-full max-w-sm text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 bg-[#235347]/10 rounded-2xl flex items-center justify-center">
              <Icon size={32} className="text-[#235347]" />
            </div>
            <h2 className="text-xl font-medium text-slate-900 mb-8">{currentStep.label}</h2>
            
            <div className="mb-4">
              <span className="text-4xl font-light text-[#235347]">
                {currentStep.key === 'sleep_hours' ? data[currentStep.key] : data[currentStep.key]}/10
              </span>
            </div>
            
            <input
              type="range"
              min={currentStep.min}
              max={currentStep.max}
              step={currentStep.key === 'sleep_hours' ? 0.5 : 1}
              value={data[currentStep.key]}
              onChange={(e) => setData(prev => ({ ...prev, [currentStep.key]: parseFloat(e.target.value) }))}
              className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#235347]"
            />
            
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>{currentStep.key === 'sleep_hours' ? '0h' : 'Low'}</span>
              <span>{currentStep.key === 'sleep_hours' ? '12h' : 'High'}</span>
            </div>

            <div className="flex gap-3 mt-10">
              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="flex-1 border border-slate-200 text-slate-700 rounded-full px-6 py-3 font-medium hover:bg-slate-50 transition-colors"
                >
                  Back
                </button>
              )}
              <button
                data-testid="checkin-next"
                onClick={() => setStep(step + 1)}
                className="flex-1 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] transition-colors"
              >
                {step < steps.length - 1 ? 'Next' : 'Add Notes'}
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm"
          >
            <h2 className="text-xl font-medium text-slate-900 mb-4 text-center">Any notes for today?</h2>
            <textarea
              data-testid="checkin-notes"
              value={data.notes}
              onChange={(e) => setData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Optional: How you're feeling, any symptoms, things on your mind..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 min-h-[120px] resize-none focus:ring-2 focus:ring-[#235347]/20 focus:border-[#235347] outline-none"
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep(step - 1)}
                className="flex-1 border border-slate-200 text-slate-700 rounded-full px-6 py-3 font-medium hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
              <button
                data-testid="checkin-submit"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Spinner size={20} className="animate-spin" />}
                Complete
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Progress indicator */}
      <div className="px-6 pb-8">
        <div className="flex gap-2 justify-center">
          {[...steps, { key: 'notes' }].map((_, idx) => (
            <div
              key={idx}
              className={`h-1 w-8 rounded-full transition-colors ${
                idx <= step ? 'bg-[#235347]' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};


export default CheckInPage;
